/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import Textfield, { Theme } from 'components/textfield';
import { VatCodeSelect } from 'components/select';
import { DatePicker } from 'components/date-picker';
import { Grid, GridColumn } from 'components/page';
import { Field, Form, FieldArray, useForm, MoneyInput } from 'components/form';
import Textarea from '@atlaskit/textarea';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EditIcon from '@atlaskit/icon/glyph/edit';
import AddIcon from '@atlaskit/icon/glyph/add';
import Button from 'components/button';
import { FormRenderProps } from 'react-final-form';
import { FormattedMessage, FormattedNumber, useIntl } from 'components/i18n';
import { HelperMessage } from '@atlaskit/form';
import { DateTime } from 'luxon';
import CustomerSelect from './components/CustomerSelect';
import { FormSpy } from '../../components/form';
import { Sale, SaleItem, VatCode } from './types';
import SaleItemSuggest from './components/SaleItemSuggest';
import useSaleItemModal from './components/useSaleItemModal';
import { colors } from '@atlaskit/theme';
import { useModal } from '../../components/modal-dialog';
import IntervalUnitInput from './components/IntervalUnitInput';
import TextAreaWithVariables from './components/TextAreaWithVariables';
import { useAuth } from "../auth";
import { useBlocker } from "../../libs/useBlocker";
import DropdownMenu, { DropdownItem, DropdownItemGroup } from "@atlaskit/dropdown-menu";
import MoreIcon from "@atlaskit/icon/glyph/more";
import RecentIcon from '@atlaskit/icon/glyph/recent'
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent'

export interface Props extends FormRenderProps<any> {
  testId?: string;
  isSaving: boolean;
  isRecurring: boolean;
  isNew: boolean;
  getDefaultVatCode?: (sale: Sale) => VatCode;
}

interface THProps {
  width: number;
  align?: 'left' | 'right';
}

const TH = styled.th`
  width: ${({ width }: THProps) => width}%;
  text-align: ${({ align = 'left' }: THProps) => align};
`;
interface TDProps {
  align?: 'left' | 'right';
}
const TD = styled.td`
  text-align: ${({ align = 'left' }: TDProps) => align};
  input {
    text-align: ${({ align = 'left' }: TDProps) => align};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TR = styled.tr`
  .visible-on-row-hover {
    opacity: 0;
  }
  &:hover {
    .visible-on-row-hover {
      opacity: 1;
    }
  }
`
const PaymentTerm = () => (
  <FormSpy<Sale> subscription={{ values: true }}>
    {({ values: { invoiceDate, dueDate, paymentTerm } }) => {
      if (!invoiceDate || !dueDate) return null;
      return (
        <div
          css={css`
            margin-bottom: -1rem;
          `}
        >
          <HelperMessage>
            {paymentTerm} {/*{*/}
            {/*  Math.round(DateTime.fromISO(dueDate!).diff(*/}
            {/*    DateTime.fromISO(invoiceDate!),*/}
            {/*    'days'*/}
            {/*  ).days)*/}
            {/*}{' '}*/}
            <FormattedMessage id="sale.SaleForm.days" defaultMessage="days" />
          </HelperMessage>
        </div>
      );
    }}
  </FormSpy>
);

function useCustomPrompt(messageObj: string, shouldPrompt: boolean | Function) {
  const [showModal] = useModal();
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const retryFn = useRef(() => {});
  useEffect(() => {
    if (confirmedNavigation) {
      retryFn.current();
    }
  }, [confirmedNavigation]);

  const handleBlockNavigation = ({ retry }: { retry: () => any }) => {
    const shouldDisplayPrompt =
      typeof shouldPrompt === 'boolean' ? shouldPrompt : shouldPrompt();
    if (shouldDisplayPrompt) {
      confirm('Are you sure');
    } else {
      retry();
    }
  };

  useBlocker(handleBlockNavigation, !confirmedNavigation);
}

const SalesForm: React.FC<Props> = ({
  isNew,
  isRecurring,
  values: sale,
  handleSubmit,
  isSaving,
  pristine,
  getDefaultVatCode,
  form: {
    mutators: { push, pop },
    change,
    getState,
  },
}) => {
  const intl = useIntl();
  const { isBookkeeper, isAdmin } = useAuth();

  // useCustomPrompt('Are you sure you want to leave?', !pristine);
  const showSaleItemModal = useSaleItemModal();

  // const setItem = useCallBack((item) => {
  //
  // };

  console.log('sale.customer', sale?.customer);

  const addLine = useCallback(() => {
    if (!getDefaultVatCode) return;
    const saleData = getState().values;

    const defaultVatCode = getDefaultVatCode(saleData);

    push('Lines', {
      description: '',
      vatCode: defaultVatCode,
      vatCodeId: defaultVatCode.id,
      price: null,
      quantity: 1,
      amount: null,
    });
  }, []);

  const changeItem = useCallback((item, line: string) => {
    change(`${line}.item`, item);
    if (!item) {
      change(`${line}.name`, null);
      change(`${line}.itemId`, null);
      return;
    }
    change(`${line}.itemId`, item.id);
    change(`${line}.name`, item.name);
    change(`${line}.description`, item.description);
    change(`${line}.price`, item.price);
    change(`${line}.vatCodeId`, item.vatCodeId);
    change(`${line}.quantity`, 1);
  }, []);

  const onItemEdit = useCallback((item, name: string) => {
    showSaleItemModal({
      saleItem: item,
      onSubmit: async (saleItem: SaleItem) => {
        changeItem(saleItem, name);
        // TODO update all lines with this item
      },
    });
  }, []);

  console.log('sale form rendered');

  return (
    <form onSubmit={handleSubmit}>
      {/*<Prompt*/}
      {/*  when={!pristine}*/}
      {/*  message="Are you sure you want to leave?"*/}
      {/*/>*/}
      <div
        css={css`
          max-width: 500px;
        `}
      >
        <Field
          label={
            <FormattedMessage
              id="sale.SaleForm.customer"
              defaultMessage="Customer"
            />
          }
          name="customer"
        >
          <CustomerSelect />
        </Field>
        <Grid spacing="cosy">
          {!isRecurring ? (
            <React.Fragment>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.invoiceNumber"
                      defaultMessage="Invoice number"
                    />
                  }
                  name="invoiceNumber"
                  component={Textfield}
                  isDisabled={sale?.status && sale?.status !== 'draft'}
                />
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.invoiceDate"
                      defaultMessage="Invoice Date"
                    />
                  }
                  name="invoiceDate"
                  component={DatePicker}
                />
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.dueDate"
                      defaultMessage="Due Date"
                    />
                  }
                  name="dueDate"
                >
                  {({ input }) => {
                    return (
                      <div>
                        <DatePicker {...input} />
                        <PaymentTerm />
                      </div>
                    );
                  }}
                </Field>
              </GridColumn>
              {isBookkeeper && <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.journalDate"
                      defaultMessage="Journal Date"
                    />
                  }
                  name="journalDate"
                  component={DatePicker}
                 />
              </GridColumn>}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.invoiceNumber"
                      defaultMessage="Invoice number"
                    />
                  }
                  name="invoiceNumber"
                  isDisabled // TODO base on invoice status (concept, final, etc.)
                  value={intl.formatMessage({
                    id: 'sale.SaleForm.automatic',
                    defaultMessage: 'Automatic',
                  })}
                  component={Textfield}
                >
                  {/*{() => <span>Automatic</span>}*/}
                </Field>
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.startDate"
                      defaultMessage="Start Date"
                    />
                  }
                  name="startDate"
                  component={DatePicker}
                />
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.SaleForm.endDate"
                      defaultMessage="End Date"
                    />
                  }
                  name="endDate"
                  placeholder={intl.formatMessage({
                    id: 'sale.SaleForm.never',
                    defaultMessage: 'never',
                  })}
                  component={DatePicker}
                />
              </GridColumn>
              <GridColumn medium={6}>
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      width: 3rem;
                      margin-right: 0.5rem;
                    `}
                  >
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.SaleForm.every"
                          defaultMessage="Every"
                        />
                      }
                      name="intervalNumber"
                      component={Textfield}
                    />
                  </div>
                  <div
                    css={css`
                      flex: 1;
                      padding-top: 3px;
                    `}
                  >
                    <Field
                      label={' '}
                      name="intervalUnit"
                      component={IntervalUnitInput}
                    />
                  </div>
                </div>
              </GridColumn>
            </React.Fragment>
          )}
          <GridColumn medium={12}>
            <Field
              label={
                <FormattedMessage
                  id="sale.SaleForm.description"
                  defaultMessage="Description"
                />
              }
              name="description"
              placeholder={intl.formatMessage({
                id: 'sale.SaleForm.optional',
                defaultMessage: 'optional',
              })}
              component={TextAreaWithVariables}
              rows={1}
              minimumRows={1}
            />
          </GridColumn>
        </Grid>
      </div>
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <table
          css={css`
            td,
            th {
              padding: 4px 4px;
            }
          `}
        >
          <thead>
            <TR>
              <TH width={50}>
                <FormattedMessage
                  id="sale.SaleForm.item"
                  defaultMessage="Item"
                />
              </TH>
              <TH width={4} align="right">
                <FormattedMessage
                  id="sale.SaleForm.quantity"
                  defaultMessage="Quantity"
                />
              </TH>
              <TH width={15} align="right">
                <FormattedMessage
                  id="sale.SaleForm.price"
                  defaultMessage="Price"
                />
              </TH>
              <TH width={25}>
                <FormattedMessage id="sale.SaleForm.vat" defaultMessage="VAT" />
              </TH>
              {isAdmin && <TH width={4} align="right" >
                <EmojiFrequentIcon size="small" label="more" />
              </TH>}
              <TH width={1} align="right" />
            </TR>
          </thead>
          <tbody>
            <FieldArray name="Lines">
              {({ fields }) =>
                fields.map((name, index) => (
                  <TR key={name}>
                    <TD>
                      <Field<SaleItem> name={`${name}.item`}>
                        {({ input: { value: item } }) => (
                          <React.Fragment>
                            {item && (
                              <div
                                css={css`
                                  padding: 0.5rem 0.5rem 0px;
                                  display: flex;
                                  flex-direction: row;
                                  justify-content: space-between;
                                  & button {
                                    opacity: 0;
                                  }
                                  &:hover button {
                                    opacity: 1;
                                  }
                                `}
                              >
                                <div css={css``}>{item.name}</div>
                                <div
                                  css={css`
                                    display: flex;
                                  `}
                                >
                                  <Button
                                    spacing="compact"
                                    iconBefore={
                                      <EditIcon label="Edit" size="small" />
                                    }
                                    appearance="subtle"
                                    onClick={() => onItemEdit(item, name)}
                                  />
                                  <Button
                                    spacing="compact"
                                    iconBefore={
                                      <CrossIcon label="Remove" size="small" />
                                    }
                                    appearance="subtle"
                                    // @ts-ignore
                                    onClick={() => changeItem(null, name)}
                                  />
                                </div>
                              </div>
                            )}
                            <Field<string, HTMLTextAreaElement>
                              name={`${name}.description`}
                            >
                              {({ input }) => (
                                <SaleItemSuggest
                                  onSelect={(saleItem) => {
                                    console.log('item is', `${name}.item`);
                                    // @ts-ignore
                                    changeItem(saleItem, name);
                                    input.onChange(saleItem.description);
                                  }}
                                  shouldSuggest={!item}
                                  appearance="subtle"
                                  {...input}
                                />
                              )}
                            </Field>
                          </React.Fragment>
                        )}
                      </Field>
                    </TD>
                    <TD align="right">
                      <Field name={`${name}.quantity`} component={Textfield}>
                        {({ input }) => (
                          <Textfield
                            type="number"
                            appearance="subtle"
                            {...input}
                          />
                        )}
                      </Field>
                    </TD>
                    <TD align="right">
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-end;
                          minwidth: 80px;
                        `}
                      >
                        <Field
                          name={`${name}.price`}
                          appearance="subtle"
                          component={MoneyInput}
                        />
                      </div>
                    </TD>
                    <TD>
                      <FormSpy<Sale> subscription={{ values: true }}>
                        {({ values: { invoiceDate, customer } }) => (
                          <Field name={`${name}.vatCodeId`}>
                            <VatCodeSelect
                              sale
                              date={invoiceDate}
                              customer={customer}
                            />
                          </Field>
                        )}
                      </FormSpy>
                    </TD>
                    {isAdmin && <TD align={'right'}>
                      <Field name={`${name}.removesAfter`} component={Textfield}>
                        {({ input }) => (
                          <Textfield
                            style={{width: 32}}
                            type="number"
                            isCompact
                            placeholder="∞"
                            appearance="subtle"
                            {...input}
                          />
                        )}
                      </Field>
                    </TD>}
                    <TD align="right">
                      <ActionButtons> <Button
                        iconBefore={<CrossIcon size="small" label="Remove" />}
                        appearance="subtle"
                        onClick={() => fields.remove(index)}
                      />
                        {/*<Button*/}
                        {/*  className={'visible-on-row-hover'}*/}
                        {/*  size="small"*/}
                        {/*  style={{position: 'absolute', paddingLeft: 2,*/}
                        {/*    paddingRight: 2}}*/}
                        {/*  appearance="subtle"*/}
                        {/*  iconBefore={<EmojiFrequentIcon size="small" label="more" />}*/}
                        {/*></Button>*/}
                      {/*<DropdownMenu*/}
                      {/*  trigger={({ triggerRef, ...props }) => (*/}
                      {/*    */}
                      {/*  )}*/}
                      {/*>*/}
                      {/*  <DropdownItemGroup>*/}
                      {/*    <DropdownItem>Edit</DropdownItem>*/}
                      {/*    <DropdownItem>Share</DropdownItem>*/}
                      {/*    <DropdownItem>Move</DropdownItem>*/}
                      {/*  </DropdownItemGroup>*/}
                      {/*</DropdownMenu>*/}
                      </ActionButtons>
                    </TD>
                  </TR>
                ))
              }
            </FieldArray>
          </tbody>
          <FormSpy<Sale> subscription={{ values: true }}>
            {({ values: { totalAmount, subtotalAmount, totalVatAmounts } }) => {
              return (
                <tfoot
                  css={css`
                    & {
                      border-bottom: none;
                    }
                    td {
                      text-align: right;
                      padding: 0.5rem 0rem;
                    }
                  `}
                >
                  <tr>
                    <td
                      colSpan={2}
                      css={css`
                        & {
                          text-align: left !important;
                        }
                      `}
                    >
                      <div>
                        <Button
                          appearance="subtle"
                          iconBefore={<AddIcon label="Add" size="medium" />}
                          onClick={() => addLine()}
                        />
                      </div>
                    </td>
                    <td>
                      <FormattedMessage
                        id="sale.SaleForm.subtotal"
                        defaultMessage="Subtotal"
                      />
                    </td>
                    <td>
                      <FormattedNumber
                        value={subtotalAmount || 0}
                        currency="EUR"
                      />
                    </td>
                  </tr>
                  {totalVatAmounts?.map(
                    ({ vatCodeId, displayName, amount }) => (
                      <tr key={vatCodeId}>
                        <td colSpan={3}>
                          <FormattedMessage
                            id="sale.SaleForm.vat"
                            defaultMessage="VAT"
                          />{' '}
                          {displayName}
                        </td>
                        <td>
                          <FormattedNumber value={amount || 0} currency="EUR" />
                        </td>
                      </tr>
                    )
                  )}
                  <tr
                    css={css`
                      td {
                        border-top: 1px solid ${colors.N40};
                      }
                      font-weight: 600;
                    `}
                  >
                    <td
                      colSpan={2}
                      css={css`
                        & {
                          border-top: none !important;
                        }
                      `}
                    />
                    <td>
                      <FormattedMessage
                        id="sale.SaleForm.total"
                        defaultMessage="Total"
                      />
                    </td>
                    <td>
                      <FormattedNumber
                        value={totalAmount || 0}
                        currency="EUR"
                        style="currency"
                      />
                    </td>
                    <td />
                  </tr>
                </tfoot>
              );
            }}
          </FormSpy>
        </table>
      </div>

      <Grid spacing="cosy">
        <GridColumn medium={12}>
          <Field
            label={
              <FormattedMessage id="sale.SaleForm.note" defaultMessage="Note" />
            }
            name="note"
            component={TextAreaWithVariables}
            appearance={'subtle'}
            minimumRows={3}
          />
        </GridColumn>
      </Grid>
      <Grid spacing="cosy">
        <GridColumn medium={12}>
          <Field
            label={
              <FormattedMessage
                id="sale.SaleForm.footer"
                defaultMessage="Footer"
              />
            }
            name="footer"
            component={TextAreaWithVariables}
            appearance={'subtle'}
            rows={1}
            minimumRows={1}
          />
        </GridColumn>
      </Grid>
    </form>
  );
};

export default SalesForm;
